import React from 'react';
import { AnimatedSection } from '../ui/AnimatedSection';
import { inlineEm } from './inlineEm';

interface Props {
  /** Top-rule mono marker (the small "§ Outcomes" / "Nº 03 / Catalogue" label). */
  marker: string;
  /** Sub-line under the marker (e.g. "What you leave with"). Optional. */
  rubric?: string;
  /** Main headline. Supports `*word*` markdown. */
  headline: string;
  /** Optional one-paragraph subhead. */
  subhead?: string;
  /** Bottom margin step. Default md. */
  spacing?: 'sm' | 'md';
}

/** Shared 2-column section header used across editorial + utility blocks.
 *  Mono §-marker on the left with a top rule, serif headline + subhead on the right. */
export const MarketingSectionHeader: React.FC<Props> = ({
  marker,
  rubric,
  headline,
  subhead,
  spacing = 'md',
}) => {
  const mb = spacing === 'sm' ? 'mb-10' : 'mb-12 md:mb-16';
  return (
    <AnimatedSection className={`grid md:grid-cols-[220px_1fr] gap-8 md:gap-12 items-baseline ${mb}`}>
      <div className="text-xs font-mono uppercase tracking-[0.14em] text-gray-500 border-t-2 border-gray-900 pt-3">
        <span className="block text-primary-600 font-medium mb-1.5">{marker}</span>
        {rubric}
      </div>
      <div>
        <h2 className="font-serif text-3xl md:text-5xl text-gray-900 leading-[1.05] tracking-tight mb-4 max-w-[20ch] text-balance">
          {inlineEm(headline)}
        </h2>
        {subhead && (
          <p className="text-lg text-gray-500 max-w-[52ch] leading-relaxed">{subhead}</p>
        )}
      </div>
    </AnimatedSection>
  );
};
